import {cx as addStyles, css} from '@emotion/css';
import {History} from 'history';
import {assocPath, findIndex, mergeDeepLeft, propEq, toPairs} from 'ramda';
import React, {useEffect, useState} from 'react';
import {AuctionBoard} from 'src/Components/AuctionBoard';
import {
  CategoriesDb,
  Constants,
  CustomerItem,
  CustomerItemStatus,
  CustomerPortalItemsTab,
  EMPTY_USER,
  Loading,
  MultiSelect,
  Options,
  Pagination,
  Select,
  User,
  ZEUS_ID,
  ZEUS_NO_SALE_ID,
  addMinutes,
  apiCall,
  hostEndpoint,
  isValidItemPickupTime,
  isoNow,
  marketToTimezone,
  remoovNodeEndpoint,
  replaceTimezone,
  roundToNearestMinutes,
  theme,
  url,
} from '../../Common';
import {
  ConsignmentBoard,
  DonationBoard,
  ExpiredBoard,
  Footer,
  Menu,
  ReturnModal,
  StatusMeaningModal,
} from '../../Components';

const {
  NewSiteUrlJs: {YOUR_QUOTE_URL},
  NewCustomerPortalUrl: {
    BETA_ITEMS_URL,
    BETA_EXPIRED_ITEMS_URL,
    BETA_DASHBOARD_URL,
    BETA_DONATION_ITEMS_URL,
    BETA_RECYCLE_ITEMS_URL,
    BETA_AUCTION_ITEMS_URL,
  },
  EndOfConsignmentAction: {DONATE, RETURN},
} = Constants;
const DEFAULT_TAB: CustomerPortalItemsTab = 'consignment';
export type CustomerPortalMyItemsState = {
  history?: History;
  expiredItemsCount: number;
  pickupOptions: Options<Maybe<number>>;
  items: CustomerItem[];
  originalItems: CustomerItem[];
  total_pages: number;
  showItemDetailsModal: Maybe<number>;
  showStatusMeanModal: boolean;
  itemDetailsModalId: Maybe<string>;
  showSaveNotes: Maybe<number>;
  showNotMyItem: Maybe<number>;
  showRequestReturn: Maybe<number>;
  user: User;
  pendingBalance: boolean;
  returnModal: ReturnModal;
  pageLoading: boolean;
  actionSelected?: Maybe<string>;
  donationItems: CustomerItem[];
  recycleItems: CustomerItem[];
  expiredItems: CustomerItem[];
  itemsCount: Record<CustomerPortalItemsTab, number>;
};
export type CustomerPortalMyItemsProps = {
  resaleType: Maybe<CustomerPortalItemsTab>;
  history: History;
  location: {
    pathname: string;
    search: string;
    query: {
      pickupId: string;
      page: string;
      sort: string;
      status: string;
      categoryIds: string;
    };
  };
};
export const CustomerPortalMyItems = ({
  resaleType,
  history,
  location: {
    query,
    query: {pickupId, page: pageProp, sort: sortProp, status, categoryIds},
  },
}: CustomerPortalMyItemsProps) => {
  const sort = sortProp || 'status_asc';
  const page = pageProp || 1;
  const [state, setState] = useState<CustomerPortalMyItemsState>({
    expiredItemsCount: 0,
    pickupOptions: [],
    items: [],
    originalItems: [],
    total_pages: 0,
    showItemDetailsModal: null,
    showSaveNotes: null,
    showNotMyItem: null,
    showRequestReturn: null,
    showStatusMeanModal: false,
    itemDetailsModalId: '',
    user: EMPTY_USER,
    pendingBalance: false,
    returnModal: initialReturnModal(),
    donationItems: [],
    recycleItems: [],
    expiredItems: [],
    pageLoading: true,
    actionSelected: null,
    itemsCount: {auction: 0, consignment: 0, donation: 0, expired: 0, recycle: 0},
  });
  const optionTable: CustomerPortalItemsTab = resaleType || DEFAULT_TAB;
  const {
    expiredItemsCount,
    pickupOptions,
    items,
    originalItems,
    total_pages,
    showItemDetailsModal,
    showSaveNotes,
    showNotMyItem,
    showRequestReturn,
    showStatusMeanModal,
    user,
    returnModal,
    pageLoading,
  } = state;

  const historyPushWithQuery = path => history.push(url([path], query));

  const selectPickupOptions: Options<Maybe<number>> = [
    {label: '', value: null},
    ...pickupOptions,
  ].filter(option => option.label !== null && option.label !== undefined);

  const categories = CategoriesDb.map(({name, id}) => ({
    label: name,
    value: id,
  }));
  useEffect(() => {
    apiCall('loadCustomerTabsItemsCount', {
      pickupId: pickupId ? parseInt(pickupId) : undefined,
    }).then(itemsCount => {
      const initialTab: CustomerPortalItemsTab = (
        [
          itemsCount.consignment && 'consignment',
          itemsCount.auction && 'auction',
          itemsCount.donation && 'donation',
          itemsCount.recycle && 'recycle',
          itemsCount.expired && 'expired',
          DEFAULT_TAB,
        ] as const
      ).filter(Boolean)[0];
      const path = {
        auction: BETA_AUCTION_ITEMS_URL,
        consignment: BETA_ITEMS_URL,
        donation: BETA_DONATION_ITEMS_URL,
        recycle: BETA_RECYCLE_ITEMS_URL,
        expired: BETA_EXPIRED_ITEMS_URL,
      }[initialTab];
      if (path !== location.pathname) historyPushWithQuery(path);
      setState(mergeDeepLeft({itemsCount}));
    });
  }, [pickupId]);
  useEffect(() => {
    reload();
  }, [resaleType, pickupId, pageProp, sort, status, categoryIds]);

  useEffect(() => {
    const handleKeyUp = ({key}: KeyboardEvent) => {
      if (key === 'Escape')
        setState(
          mergeDeepLeft({
            showItemDetailsModal: null,
            showSaveNotes: null,
            showNotMyItem: null,
            showRequestReturn: null,
            showStatusMeanModal: false,
          }),
        );
    };
    window.addEventListener('keyup', handleKeyUp);
    return () => window.removeEventListener('keyup', handleKeyUp);
  }, []);

  const reload = async () => {
    setState(mergeDeepLeft({pageLoading: true}));
    const tabs: CustomerPortalItemsTab[] = [
      'donation',
      'recycle',
      'expired',
      'consignment',
      'auction',
    ];
    try {
      if (!tabs.includes(optionTable)) {
        if (window.location.pathname !== BETA_ITEMS_URL) historyPushWithQuery(BETA_ITEMS_URL);
        return;
      }
      const [{page, total_pages, items, expiredItemsCount, user}, {pickupOptions}] =
        await Promise.all([
          apiCall('loadCustomerPortalItemsPage', {
            itemTab: optionTable,
            pickupId: parseInt(pickupId) || null,
            page: parseInt(pageProp) || 1,
            sort,
            status: status ? status.split(',') : null,
            categoryIds: categoryIds ? categoryIds.split(',').map(n => parseInt(n)) : null,
          }),
          apiCall('loadCustomerPortalPickupOptions'),
        ]);
      setState(
        // @ts-ignore TODO: fix this type error
        mergeDeepLeft({
          page,
          total_pages,
          items,
          originalItems: items,
          pickupOptions,
          showItemDetailsModal: null,
          showSaveNotes: null,
          showNotMyItem: null,
          showRequestReturn: null,
          showStatusMeanModal: false,
          itemDetailsModalId: null,
          returnModal: initialReturnModal(),
          expiredItemsCount,
          pageLoading: false,
          user,
        }),
      );
    } catch (error) {
      alert(String(error) || 'Request failed, please retry.');
      setState(mergeDeepLeft({pageLoading: false}));
    }
  };
  function initialReturnModal(): ReturnModal {
    let date = roundToNearestMinutes(isoNow(), {nearestTo: 30});
    while (!isValidItemPickupTime(date)) {
      date = addMinutes(date, 30);
    }
    return {
      item: null,
      date,
    };
  }
  const donateItem = async (item: CustomerItem) => {
    try {
      if (!item.id) return;
      setState(mergeDeepLeft({actionSelected: DONATE}));
      await apiCall('requestItemDonation', item.id);
      await reload();
    } catch (error) {
      alert(String(error) || 'Request failed, please retry.');
    }
  };
  const returnItem = async () => {
    const {item, date} = returnModal;
    if (!item) return;
    if (!item.id) return;
    try {
      setState(mergeDeepLeft({actionSelected: RETURN}));
      await apiCall('requestItemReturn', {
        id: item.id,
        timestamp: replaceTimezone(date, marketToTimezone(item.market)),
      });
      await reload();
    } catch (error) {
      alert(String(error) || 'Server error. Please try again.');
    }
  };
  const notMyItem = async (item: CustomerItem) => {
    try {
      if (!item.id || !item.type) return;
      await apiCall('customerSendMessage', {
        type: item.type,
        id: item.id,
        message_type: 'not_my_item',
      });
      setState(mergeDeepLeft({showNotMyItem: item.id, showItemDetailsModal: null}));
    } catch (error) {
      alert(String(error) || 'Request failed, please retry.');
    }
  };
  const requestReturn = async (item: CustomerItem) => {
    try {
      if (!item.id || !item.type) return;
      await apiCall('customerSendMessage', {
        type: item.type,
        id: item.id,
        message_type: 'request_return',
      });
      setState(mergeDeepLeft({showRequestReturn: item.id, showItemDetailsModal: null}));
    } catch (error) {
      alert(String(error) || 'Request failed, please retry.');
    }
  };
  const sortDirection = sort.split('_').pop();
  const sortColumn = sort.split('_').slice(0, -1).join('_');
  const setSort = (column: string) => {
    // @ts-ignore TODO: fix this type error
    setState(mergeDeepLeft({items: []}));
    const direction = sortDirection === 'asc' && sortColumn === column ? 'desc' : 'asc';
    history.push(
      url([window.location.pathname], {
        ...query,
        page: null,
        sort: `${column}_${direction}`,
      }),
    );
  };
  const goToPage = (page: number) => {
    history.push(url([window.location.pathname], {...query, page, sort: null}));
  };
  const setPublicNotes = (id: Maybe<number>, value: string) => {
    const index = items?.findIndex(propEq('id', id));
    if (index === undefined || index < 0) return;
    setState(assocPath(['items', index, 'public_notes'], value));
  };
  const savePublicNotes = async (item: CustomerItem) => {
    try {
      if (!item.type || !item.id) return;
      await apiCall('customerAddNotes', {
        type: item.type,
        id: item.id,
        notes: item.public_notes || '',
      });
      const index = findIndex(propEq('id', item.id), state.originalItems);
      if (index >= 0) {
        setState(assocPath(['originalItems', index, 'public_notes'], item.public_notes));
      }
      setState(mergeDeepLeft({showSaveNotes: item.id}));
    } catch (error) {
      alert(String(error) || 'Request failed, please retry.');
    }
  };
  const selectStatus = (status?: string[] | null) => {
    history.push(url([window.location.pathname], {...query, page: null, status}));
  };
  const selectCategory = (categoryIds?: number[] | null) => {
    history.push(url([window.location.pathname], {...query, page: null, categoryIds}));
  };
  return (
    <Container>
      <Menu
        dashboardLink={() => history.push(BETA_DASHBOARD_URL)}
        myItemsLink={() => history.push(BETA_ITEMS_URL)}
        newPickupLink={() => {
          if (user.owner_id)
            // @ts-ignore window.location
            window.location = url([hostEndpoint(), YOUR_QUOTE_URL.slice(1)], {
              email: user?.email,
            });
          history.push(BETA_DASHBOARD_URL);
        }}
        logoutLink={async () => {
          const response = await fetch(`${remoovNodeEndpoint()}/sign-out`, {
            method: 'POST',
            headers: {'Content-type': 'application/json'},
            credentials: 'include',
            redirect: 'follow',
          });
          const {url} = await response.json();
          // @ts-ignore
          window.location = url;
        }}
      />
      {pageLoading ? (
        <div style={{margin: '100px 0'}}>
          <Loading width={100} height={100} type='spinningBubbles' color={theme.colors.darkBlue} />
        </div>
      ) : (
        <Content>
          <TopContainer>
            <Title>PICKUP</Title>
            <Select
              isLarge
              value={parseInt(pickupId) || null}
              options={selectPickupOptions}
              onChange={pickupId => history?.push(url([location.pathname], {...query, pickupId}))}
            />
            {optionTable === 'consignment' &&
              [ZEUS_ID, ZEUS_NO_SALE_ID].includes(user.owner_id) && (
                <>
                  <Title>STATUS</Title>
                  <MultiSelect
                    options={toPairs(CustomerItemStatus)
                      .filter(([_, value]) => value !== 'Free')
                      .map(([_, value]) => ({label: value, value}))}
                    value={status?.split(',')}
                    onChange={selectStatus}
                    isLarge
                  />
                  <Title>CATEGORY</Title>
                  <MultiSelect
                    options={categories}
                    value={categoryIds?.split(',').map(n => parseInt(n))}
                    onChange={selectCategory}
                    isLarge
                  />
                </>
              )}
          </TopContainer>

          <TablesOptionsContainer>
            {!!state.itemsCount.consignment && (
              <OptionContainer>
                <Option
                  onClick={() => historyPushWithQuery(BETA_ITEMS_URL)}
                  selected={optionTable === 'consignment'}
                >
                  CONSIGNMENT
                </Option>
                {optionTable === 'consignment' && <Line />}
              </OptionContainer>
            )}
            {!!state.itemsCount.auction && (
              <OptionContainer className='needMargin'>
                <Option
                  onClick={() => historyPushWithQuery(BETA_AUCTION_ITEMS_URL)}
                  selected={optionTable === 'auction'}
                >
                  AUCTION
                </Option>
                {optionTable === 'auction' && <Line />}
              </OptionContainer>
            )}
            {!!state.itemsCount.donation && (
              <OptionContainer className='needMargin'>
                <Option
                  onClick={() => historyPushWithQuery(BETA_DONATION_ITEMS_URL)}
                  selected={optionTable === 'donation'}
                >
                  DONATION
                </Option>
                {optionTable === 'donation' && <Line />}
              </OptionContainer>
            )}
            {!!state.itemsCount.recycle && (
              <OptionContainer className='needMargin'>
                <Option
                  onClick={() => historyPushWithQuery(BETA_RECYCLE_ITEMS_URL)}
                  selected={optionTable === 'recycle'}
                >
                  RECYCLE
                </Option>
                {optionTable === 'recycle' && <Line />}
              </OptionContainer>
            )}
            {!!state.itemsCount.expired && (
              <OptionContainer className='needMargin'>
                <Option
                  onClick={() => historyPushWithQuery(BETA_EXPIRED_ITEMS_URL)}
                  selected={optionTable === 'expired'}
                  className='expired'
                >
                  EXPIRED
                  <ExpiredCounter isSelected={optionTable === 'expired'}>
                    {expiredItemsCount}
                  </ExpiredCounter>
                </Option>
                {optionTable === 'expired' && <Line />}
              </OptionContainer>
            )}
            {optionTable === 'donation' && (
              <ExplanationText>
                Here are the items that have been donated on your behalf.
              </ExplanationText>
            )}
            {optionTable === 'recycle' && (
              <ExplanationText>
                Here are the items that have been recycled on your behalf.
              </ExplanationText>
            )}
            {optionTable === 'expired' && (
              <ExplanationText>
                Unfortunately the following items did not sell during the consignment period. Please
                select if we should donate these items or if you would like to pick them up.
              </ExplanationText>
            )}
          </TablesOptionsContainer>
          {optionTable === 'consignment' && (
            <ConsignmentBoard
              items={items || []}
              originalItems={originalItems}
              isOpenModal={showItemDetailsModal}
              setIsOpenModal={id => setState(mergeDeepLeft({showItemDetailsModal: id}))}
              isSaveNotesModal={showSaveNotes}
              setIsSaveNotesModal={id => setState(mergeDeepLeft({showSaveNotes: id}))}
              isNotMyItemModal={showNotMyItem}
              setNotMyItemModal={id => setState(mergeDeepLeft({showNotMyItem: id}))}
              isRequestReturnModal={showRequestReturn}
              setRequestReturnModal={id => setState(mergeDeepLeft({showRequestReturn: id}))}
              sort={sortColumn}
              sortDirection={sortDirection === 'desc' ? 'desc' : 'asc'}
              setSort={setSort}
              setStatusModal={() => setState(mergeDeepLeft({showStatusMeanModal: true}))}
              setPublicNotes={(id: Maybe<number>) => (value: string) => setPublicNotes(id, value)}
              savePublicNotes={savePublicNotes}
              categories={categories}
              notMyItem={notMyItem}
              requestReturn={requestReturn}
            />
          )}
          {optionTable === 'auction' && (
            <AuctionBoard
              items={items || []}
              originalItems={originalItems || []}
              categories={categories}
              isOpenModal={showItemDetailsModal}
              setIsOpenModal={id => setState(mergeDeepLeft({showItemDetailsModal: id}))}
              sort={sortColumn}
              sortDirection={sortDirection === 'desc' ? 'desc' : 'asc'}
              setSort={setSort}
              setStatusModal={() => setState(mergeDeepLeft({showStatusMeanModal: true}))}
              setPublicNotes={(id: Maybe<number>) => (value: string) => setPublicNotes(id, value)}
              savePublicNotes={savePublicNotes}
              isSaveNotesModal={showSaveNotes}
              setIsSaveNotesModal={id => setState(mergeDeepLeft({showSaveNotes: id}))}
            />
          )}
          {optionTable === 'donation' && (
            <DonationBoard
              items={items || []}
              itemTab='donation'
              isOpenModal={showItemDetailsModal}
              setIsOpenModal={id => setState(mergeDeepLeft({showItemDetailsModal: id}))}
              sort={sortColumn}
              sortDirection={sortDirection === 'desc' ? 'desc' : 'asc'}
              setSort={setSort}
            />
          )}
          {optionTable === 'recycle' && (
            <DonationBoard
              items={items || []}
              itemTab='recycle'
              isOpenModal={showItemDetailsModal}
              setIsOpenModal={id => setState(mergeDeepLeft({showItemDetailsModal: id}))}
              sort={sortColumn}
              sortDirection={sortDirection === 'desc' ? 'desc' : 'asc'}
              setSort={setSort}
            />
          )}
          {optionTable === 'expired' && (
            <ExpiredBoard
              items={items || []}
              isOpenModal={showItemDetailsModal}
              setIsOpenModal={id => setState(mergeDeepLeft({showItemDetailsModal: id}))}
              donateAction={donateItem}
              sort={sortColumn}
              sortDirection={sortDirection === 'desc' ? 'desc' : 'asc'}
              setSort={setSort}
              returnModal={returnModal}
              setReturnModal={(newReturnModal: RecursivePartial<ReturnModal>) =>
                // @ts-ignore RecursivePartial error
                setState(mergeDeepLeft({returnModal: newReturnModal}))
              }
              submitReturnModal={returnItem}
              notMyItem={notMyItem}
              requestReturn={requestReturn}
            />
          )}
          <Pagination goToPage={goToPage} page={page} totalPages={total_pages} />
        </Content>
      )}
      {showStatusMeanModal && (
        <StatusMeaningModal
          isOpen={showStatusMeanModal}
          setIsOpen={() => setState(mergeDeepLeft({showStatusMeanModal: !showStatusMeanModal}))}
        />
      )}
      <Footer market={items ? items[0]?.market : null} />
    </Container>
  );
};
const mediaQueries = {
  large: '@media (max-width: 1170px)',
  tablet1: '@media (max-width: 820px)',
};
const flexColumn = css({display: 'flex', flexDirection: 'column'});
const flexRow = css({display: 'flex', flexDirection: 'row'});
const center = css({alignItems: 'center', justifyContent: 'center'});
const flexStart = css({alignItems: 'flex-start', justifyContent: 'flex-start'});
function Container({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        flexColumn,
        center,
        css({
          width: '100%',
          height: '100%',
          background: theme.colors.lightBlue,
          position: 'relative',
          overflowX: 'hidden',
        }),
        className,
      )}
      {...rest}
    />
  );
}
function Content({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        flexColumn,
        flexStart,
        css({
          margin: `calc(${theme.spacings.large}*2) 0`,
          background: 'transparent',
          width: '100%',
          maxWidth: '1200px',
          padding: `0 ${theme.spacings.xxsmall}`,
        }),
        className,
      )}
      {...rest}
    />
  );
}
function TopContainer({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        flexColumn,
        flexStart,
        css({
          width: '100%',
          '> div': {maxWidth: '560px'},
          h3: {marginBottom: theme.spacings.xsmall},
        }),
        className,
      )}
      {...rest}
    />
  );
}
function Title({className, ...rest}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={addStyles(
        css({
          color: theme.colors.darkGray,
          fontSize: theme.font.sizes.small,
          fontWeight: theme.font.bold,
          fontFamily: theme.font.family,
          textAlign: 'left',
        }),
        className,
      )}
      {...rest}
    />
  );
}
function TablesOptionsContainer({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        flexRow,
        css({
          alignItems: 'center',
          marginTop: '72px',
          marginBottom: '58px',
          overflowX: 'auto',
          width: '100vw',
          height: '60px',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '> div': {marginRight: '28px'},
          '.needMargin': {margin: '0 28px'},
          '&::-webkit-scrollbar': {display: 'none'},
        }),
        className,
      )}
      {...rest}
    />
  );
}
function OptionContainer({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(flexColumn, center, css({position: 'relative'}), className)}
      {...rest}
    />
  );
}
type OptionProps = {
  selected?: boolean;
} & React.HTMLAttributes<HTMLButtonElement>;
function Option({selected, className, ...rest}: OptionProps) {
  return (
    <button
      className={addStyles(
        flexRow,
        center,
        css({
          color: selected ? theme.colors.orange : theme.colors.darkGray,
          fontFamily: theme.font.family,
          fontSize: theme.font.sizes.small,
          fontWeight: theme.font.bold,
          opacity: selected ? 1 : 0.8,
          cursor: 'pointer',
          background: 'transparent',
          border: 0,
          outline: 'none',
          '&:hover ': {
            color: theme.colors.lightOrange,
            opacity: 1,
          },
        }),
        className,
      )}
      {...rest}
    />
  );
}
function Line({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        css({
          background: theme.colors.darkBlue,
          width: '80px',
          height: '2px',
          position: 'absolute',
          top: '38px',
        }),
        className,
      )}
      {...rest}
    />
  );
}
function ExplanationText({className, ...rest}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={addStyles(
        css({
          color: theme.colors.darkGray,
          fontFamily: theme.font.family,
          fontSize: theme.font.sizes.small,
          fontWeight: theme.font.normal,
          marginLeft: theme.spacings.xxlarge,
          maxWidth: '400px',
          [mediaQueries.large]: {marginLeft: '-24px'},
          [mediaQueries.tablet1]: {display: 'none'},
        }),
        className,
      )}
      {...rest}
    />
  );
}
type ExpiredCounterProps = {
  isSelected?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;
function ExpiredCounter({isSelected, className, ...rest}: ExpiredCounterProps) {
  return (
    <div
      className={addStyles(
        flexRow,
        center,
        css({
          width: '24px',
          height: '16px',
          borderRadius: '8px',
          marginBottom: '3px',
          marginLeft: theme.spacings.xxsmall,
          background: isSelected ? theme.colors.darkBlue : theme.colors.darkGray,
          fontFamily: theme.font.family,
          color: theme.colors.white,
          fontSize: theme.font.sizes.xsmall,
          fontWeight: theme.font.medium,
        }),
        className,
      )}
      {...rest}
    />
  );
}
